// Scene 3 — The day plays out. A short "open for business" beat (a filling demand
// bar driven by the same priceFactor × marketing maths), then the engine actually
// settles via AdvanceDay×5, and the cashflow result is revealed — now in the same
// neo-fintech cashflow card the HQ uses.

import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  cashflowForWeek,
  dayKeyFromDate,
  findBusiness,
  priceFactor,
  marketingBoost,
} from '@biz-kid/engine';
import { builderContent } from '@biz-kid/engine/content/builder';
import { useDispatch, useGameState } from '@biz-kid/store';
import { Card, Eyebrow, Button, Bar, Money, money } from '../../ui/kit.js';
import { StoryShell } from '../storyKit.js';

export type TheDayProps = { onComplete: () => void };

const DAY_BEAT_MS = 1400; // length of the "open for business" beat — kept short so play starts fast

export function TheDay({ onComplete }: TheDayProps) {
  const dispatch = useDispatch();
  const businesses = useGameState((s) => s.businesses);
  const currentWeek = useGameState((s) => s.clock.week);
  const business = businesses[0];
  const def = findBusiness(builderContent, 'smoothieStand');

  // Demand ratio (0..1) from the same maths the sim uses — drives the bar + count.
  const ratio = useMemo(() => {
    if (!business || !def) return 0;
    const pf = priceFactor(business.price, def.sweetPrice);
    const mb = marketingBoost(business.marketingSpend);
    return Math.max(0, Math.min(1, pf * (1 + mb) * 0.7));
  }, [business, def]);

  const [phase, setPhase] = useState<'queue' | 'settle' | 'reveal'>('queue');
  const [settledWeek, setSettledWeek] = useState<number | null>(null);

  // Skip the scene entirely if state is malformed.
  useEffect(() => {
    if (!business || !def) onComplete();
  }, [business, def, onComplete]);

  // After the day beat, run the engine settlement.
  useEffect(() => {
    if (!business || !def) return;
    const t = setTimeout(() => setPhase('settle'), DAY_BEAT_MS);
    return () => clearTimeout(t);
  }, [business, def]);

  // Drive the actual engine settle when we enter the 'settle' phase. One-shot.
  useEffect(() => {
    if (phase !== 'settle') return;
    const weekBefore = currentWeek;
    const start = new Date();
    for (let i = 0; i < 5; i++) {
      const d = new Date(start);
      d.setUTCDate(d.getUTCDate() + i + 1);
      dispatch({ kind: 'AdvanceDay', dayKey: dayKeyFromDate(d) });
    }
    setSettledWeek(weekBefore);
    const t = setTimeout(() => setPhase('reveal'), 420);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  if (!business || !def) return null;

  return (
    <StoryShell eyebrow="Day 1 · The day">
      {phase === 'reveal' && settledWeek !== null ? (
        <CashflowReveal week={settledWeek} onContinue={onComplete} />
      ) : (
        <Card>
          <Eyebrow color="var(--brand)">Open for business</Eyebrow>
          <div style={{ fontWeight: 800, fontSize: 18, color: 'var(--text)', margin: '6px 0 2px' }}>
            Selling at {money(business.price)} each
          </div>
          <p style={{ margin: '0 0 14px', fontSize: 13.5, color: 'var(--text-2)' }}>
            {phase === 'settle' ? 'Tallying up the day…' : 'Customers are stopping by…'}
          </p>
          <Bar frac={phase === 'settle' ? 1 : ratio} color="var(--brand)" />
          <div style={{ marginTop: 14 }}>
            <SaleTicker active={phase === 'queue'} ratio={ratio} price={business.price} />
          </div>
        </Card>
      )}
    </StoryShell>
  );
}

/** A little run of "+B$" pops, paced to the demand ratio — the modern stand-in for
 *  the customer parade. Purely cosmetic; the real money is settled by the engine. */
function SaleTicker({ active, ratio, price }: { active: boolean; ratio: number; price: number }) {
  const pops = Math.max(1, Math.round(ratio * 6));
  if (!active) return null;
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {Array.from({ length: pops }, (_, i) => (
        <motion.span
          key={i}
          initial={{ opacity: 0, y: 8, scale: 0.8 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: 0.25 + i * 0.4, type: 'spring', stiffness: 220, damping: 16 }}
          style={{
            fontSize: 13,
            fontWeight: 800,
            color: 'var(--up)',
            background: 'var(--up-soft)',
            padding: '4px 10px',
            borderRadius: 999,
          }}
        >
          +{money(price)}
        </motion.span>
      ))}
    </div>
  );
}

// ─── Cashflow reveal ────────────────────────────────────────────────────────

function CashflowReveal({ week, onContinue }: { week: number; onContinue: () => void }) {
  const ledger = useGameState((s) => s.ledger);
  const summary = useMemo(() => {
    const txns = ledger.filter((t) => t.week === week);
    let revenue = 0, cogs = 0, fixed = 0, marketing = 0, living = 0, units = 0;
    for (const t of txns) {
      if (t.type === 'revenue') revenue += t.amount;
      else if (t.type === 'cogs') {
        cogs += Math.abs(t.amount);
        units = Math.round(Math.abs(t.amount) / 3); // cogsPerUnit = 3
      } else if (t.type === 'fixed') fixed += Math.abs(t.amount);
      else if (t.type === 'marketing') marketing += Math.abs(t.amount);
      else if (t.type === 'living') living += Math.abs(t.amount);
    }
    return { revenue, cogs, fixed, marketing, living, units };
  }, [ledger, week]);

  const engineCashflow = useGameState((s) => cashflowForWeek(s, week));

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} style={{ display: 'grid', gap: 14 }}>
      <Card>
        <Eyebrow color="var(--brand)">Your first day</Eyebrow>
        <p style={{ margin: '6px 0 0', fontSize: 15, color: 'var(--text)', lineHeight: 1.55 }}>
          You sold <b>{summary.units}</b> drops. This is your <b>cashflow</b> — the most important number a
          business has.
        </p>
      </Card>

      <Card>
        <div style={{ display: 'grid', gap: 2 }}>
          <Row label="Revenue" value={summary.revenue} />
          <Row label="Making costs (COGS)" value={-summary.cogs} />
          <Row label="Store fees" value={-summary.fixed} />
          {summary.marketing > 0 && <Row label="Marketing" value={-summary.marketing} />}
          <Row label="Living costs" value={-summary.living} />
          <div style={{ height: 1, background: 'var(--border)', margin: '8px 0' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--text)' }}>You ended the day</span>
            <motion.span
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.6, type: 'spring', stiffness: 200 }}
            >
              <Money value={engineCashflow} signed size={30} />
            </motion.span>
          </div>
        </div>
      </Card>

      <Button tone="primary" size="lg" full onClick={onContinue}>
        LET’S GO →
      </Button>
    </motion.div>
  );
}

function Row({ label, value }: { label: string; value: number }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '5px 0' }}>
      <span style={{ fontSize: 14, color: 'var(--text-2)' }}>{label}</span>
      <Money value={value} signed size={15} />
    </div>
  );
}
